import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchConversations } from '../../hooks/teamchat';
import { TeamChatConversation } from '../../utils/types';
import {
  NewChatButton,
  TeamChatPlaceholder,
  TeamChatSearchInput,
  TeamChatShell,
  TeamChatSidebar,
  TeamChatSidebarEmpty,
  TeamChatSidebarHeader,
} from './components/teamChatUi';

export function TeamChatsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [conversations, setConversations] = useState<TeamChatConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const toast = useToast();
  const navigate = useNavigate();

  const loadConversations = async (showError = true) => {
    if (!orgId) {
      return;
    }

    try {
      const response = await fetchConversations(orgId);
      if (response.success) {
        setConversations(Array.isArray(response.data) ? response.data : []);
      } else if (showError) {
        toast({ title: 'Failed to load chats', status: 'error' });
      }
    } catch {
      if (showError) {
        toast({ title: 'Failed to load chats', status: 'error' });
      }
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!orgId) {
        return;
      }

      setLoading(true);
      await loadConversations();
      if (mounted) {
        setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [orgId, toast]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    const interval = window.setInterval(() => {
      loadConversations(false);
    }, 10000);

    return () => window.clearInterval(interval);
  }, [orgId]);

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<{ data?: Record<string, string> }>;
      if (customEvent.detail?.data?.conversation_id) {
        loadConversations(false);
      }
    };

    window.addEventListener('pipeline:notification', handleNotification as EventListener);
    return () => {
      window.removeEventListener('pipeline:notification', handleNotification as EventListener);
    };
  }, [orgId]);

  const filteredConversations = useMemo(
    () =>
      conversations.filter((conversation) => {
        const index = `${conversation.name || ''} ${conversation.recipient_name || ''} ${conversation.last_message?.content || ''}`.toLowerCase();
        return index.includes(query.toLowerCase());
      }).sort((left, right) => {
        const leftTime = left.last_message?.created_at || left.updated_at || '';
        const rightTime = right.last_message?.created_at || right.updated_at || '';
        return new Date(rightTime).getTime() - new Date(leftTime).getTime();
      }),
    [conversations, query],
  );

  return (
    <TeamChatShell
      sidebar={
        <TeamChatSidebar
          header={
            <TeamChatSidebarHeader
              title="Team chat"
              subtitle={`${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`}
              action={<NewChatButton onClick={() => navigate(`/organization/${orgId}/teamchat/select/`)} />}
            />
          }
          search={
            <TeamChatSearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search chats or start a new one"
            />
          }
          conversations={filteredConversations}
          loading={loading}
          onSelectConversation={(conversationId) =>
            navigate(`/organization/${orgId}/teamchat/conversations/${conversationId}`)
          }
          emptyState={
            <TeamChatSidebarEmpty
              title={conversations.length === 0 ? 'No chats yet' : 'No matching chats'}
              description={
                conversations.length === 0
                  ? 'Start a direct chat to begin collaborating with your team.'
                  : 'Try another name or message preview.'
              }
            />
          }
        />
      }
      thread={
        <TeamChatPlaceholder
          title="Keep the conversation flowing"
          description="Choose a chat from the left to read messages, share files, and reply without losing context."
        />
      }
    />
  );
}
